import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { AuthShell, authInput, authButton } from "@/components/auth/AuthShell";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/portal";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : redirect);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = async () => {
    if (!email) return toast.info("Enter your email first");
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If the account exists, a reset link has been sent.");
    } catch {
      toast.error("Could not send reset link");
    }
  };

  return (
    <AuthShell
      eyebrow="Candidate Portal"
      title="Sign in"
      subtitle="Access your applications, documents, and offers."
      image="https://images.unsplash.com/photo-1786520403993-e1dfdc0864b5?crop=entropy&cs=srgb&fm=jpg&q=85"
    >
      <form onSubmit={submit} className="space-y-4">
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={authInput} data-testid="login-email" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={authInput} data-testid="login-password" />
        <button type="button" onClick={forgot} className="font-sans text-xs text-[var(--aur-text-2)] hover:text-gold">Forgot password?</button>
        <button type="submit" disabled={loading} className={authButton} data-testid="login-submit">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-[var(--aur-border)]" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">or</span>
        <span className="h-px flex-1 bg-[var(--aur-border)]" />
      </div>

      <button onClick={googleLogin} data-testid="google-login-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-[var(--aur-border)] bg-white px-6 py-3 font-sans text-sm font-medium text-gray-800 transition-all hover:bg-gray-100">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
        Continue with Google
      </button>

      <p className="mt-8 text-center font-sans text-sm text-[var(--aur-text-2)]">
        New here? <Link to="/register" className="text-gold hover:text-gold-bright">Create an account</Link>
      </p>
    </AuthShell>
  );
}
